import { useId } from "react";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import type { Category } from "@/services/api/categories";
import { getCategoryLabel } from "@/features/categories/utils/categoryUtils";
import { useCategoryAttributesDisplay } from "./hooks";

interface AttributesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  category: Category | null;
}

export function AttributesModal({
  open,
  onOpenChange,
  category,
}: AttributesModalProps) {
  const { attributes, isLoading } = useCategoryAttributesDisplay({
    category,
    enabled: open,
  });
  const descriptionId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px]"
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle>Attributes Linked to "{getCategoryLabel(category)}"</DialogTitle>
          <DialogDescription>
            View all attributes associated with this category.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="py-8 text-center text-sm text-ink-500">
              Loading attributes...
            </div>
          ) : attributes && attributes.length > 0 ? (
            <div className="space-y-2">
              {attributes.map((attr) => (
                <div
                  key={String(attr.id)}
                  className="rounded-lg border border-grid-200 bg-white p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-ink-900">
                        {attr.displayName || attr.name}
                      </h4>
                      {attr.description && (
                        <p className="mt-1 text-sm text-ink-600">
                          {attr.description}
                        </p>
                      )}
                      {attr.values && attr.values.length > 0 && (
                        <div className="mt-2 text-xs text-ink-500">
                          Values:{" "}
                          {[...attr.values]
                            .sort((a, b) => a.order - b.order)
                            .map((v) => v.value)
                            .join(", ")}
                        </div>
                      )}
                      <div className="mt-2 flex flex-wrap gap-2">
                        {attr.isRequired && (
                          <span className="rounded-md bg-red-100 px-2 py-1 text-xs text-red-700">
                            Required
                          </span>
                        )}
                        {attr.isSearchable && (
                          <span className="rounded-md bg-blue-100 px-2 py-1 text-xs text-blue-700">
                            Searchable
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-ink-500">
              No attributes linked to this category.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
